import React, { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ChartCardProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** compact = grid card, focus = full-width detail */
  variant?: 'compact' | 'focus';
  /** In compact mode, fixed pixel height for the chart container so EChart with height="100%" fills it */
  compactChartHeight?: number;
  /** Click handler on the card itself (used for grid → focus navigation) */
  onClick?: () => void;
  /** When true the title/subtitle/actions header row is not rendered (used in focus mode where PageHeader owns the title) */
  hideHeader?: boolean;
  /** When set, shows an Info icon button that opens the chart definition drawer */
  onInfoClick?: () => void;
}

/**
 * Reusable card wrapper for charts. Uses app card styles and spacing (4, 8, 12, 16, 24).
 * Supports compact (grid) and focus (detail) variants.
 *
 * Chart slot heights:
 *  - compact: fixed 260px so every overview card is consistent
 *  - focus:   flex-1 (height driven by the chart component)
 */
export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  actions,
  children,
  className,
  variant = 'compact',
  compactChartHeight,
  onClick,
  hideHeader = false,
  onInfoClick,
}) => {
  const isCompact = variant === 'compact';
  const showHeader = !hideHeader;
  const chartSlotStyle = isCompact && compactChartHeight != null ? { height: compactChartHeight } : undefined;
  const actionsContent = (
    <>
      {actions}
      {onInfoClick && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick();
          }}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
          aria-label="Chart definition"
        >
          <Info className="h-4 w-4" />
        </button>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'border rounded-lg border-border bg-card flex flex-col',
        isCompact ? 'p-4' : 'p-6 min-h-0',
        onClick && 'cursor-pointer hover:border-primary/40 transition-colors',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {showHeader && (
        <div className={cn(
          'flex items-start justify-between gap-4 shrink-0',
          isCompact ? 'mb-3' : 'mb-4'
        )}>
          <div className="min-w-0">
            <h3 className={cn(
              'font-semibold text-foreground',
              isCompact ? 'text-sm' : 'text-base'
            )}>
              {title}
            </h3>
            {subtitle && (
              <p className={cn(
                'text-muted-foreground mt-0.5',
                isCompact ? 'text-xs line-clamp-1' : 'text-sm'
              )}>
                {subtitle}
              </p>
            )}
          </div>
          {(actions || onInfoClick) && <div className="shrink-0 flex items-center gap-1">{actionsContent}</div>}
        </div>
      )}
      <div
        className={cn(
          'min-h-0 flex flex-col',
          isCompact && compactChartHeight == null && 'h-[260px]',
          !isCompact && 'flex-1',
        )}
        style={chartSlotStyle}
      >
        {children}
      </div>
    </div>
  );
};
